import React, { useState, useEffect } from 'react';
import { API_BASE_URL, VAULT_TIERS, VAULT_TOKEN_VALUE } from '../constants/gameConfig';
import TodaysAirdrops from './TodaysAirdrops';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();


// ==================================================================
// fetchOnChainVaultLeaderboard (RPC ROTATION APPLIED)
// ==================================================================
async function fetchOnChainVaultLeaderboard() {
    const WAX_RPC_ENDPOINTS = [
        "https://wax.greymass.com",
        "https://api.wax.alohaeos.com",
        "https://wax.eosphere.io",
        "https://wax.eosdac.io",
        "https://wax.eu.eosamsterdam.net"
    ];

    let allRows = [];
    let more = true;
    let lowerBound = null;

    while (more) {
        const body = {
            json: true,
            code: "whatifvault1",
            scope: "whatifvault1",
            table: "stakes",
            limit: 500,
        };
        if (lowerBound !== null) body.lower_bound = lowerBound;

        let fetchSuccess = false;
        let data = null;

        // Loop through nodes!
        for (const endpoint of WAX_RPC_ENDPOINTS) {
            try {
                const response = await fetch(`${endpoint}/v1/chain/get_table_rows`, {
                    method: "POST",
                    headers: { "Content-Type": "text/plain" },
                    body: JSON.stringify(body),
                });

                if (response.ok) {
                    data = await response.json();
                    fetchSuccess = true;
                    break;
                }
            } catch (err) {
                console.warn(`Leaderboard Node ${endpoint} failed, trying next...`);
            }
        }

        if (!fetchSuccess || !data) {
            throw new Error(`All WAX RPC nodes timed out while fetching leaderboard.`);
        }

        const rows = data.rows || [];
        allRows = allRows.concat(rows);

        more = data.more === true && rows.length > 0;
        if (more) lowerBound = data.next_key;
    }

    // 1. Group stakes by user and aggregate amounts per token symbol
    const userMap = {};

    for (const row of allRows) {
        const user = row.user || row.owner || row.account;
        if (!user) continue;

        const quantityStr = row.quantity || row.staked_amount || "";
        const parts = quantityStr.split(" ");
        if (parts.length !== 2) continue;

        const amount = parseFloat(parts[0]);
        const symbol = parts[1];
        if (isNaN(amount) || amount <= 0) continue;

        if (!userMap[user]) userMap[user] = {};
        userMap[user][symbol] = (userMap[user][symbol] || 0) + amount;
    }

    // 2. Calculate vaultScore and vaultSummary
    const leaderboard = Object.entries(userMap).map(([walletAddress, tokenAmounts]) => {
        let vaultScore = 0;
        const vaultSummary = {};

        for (const [symbol, totalStakedAmount] of Object.entries(tokenAmounts)) {
            // Check if VAULT_TOKEN_VALUE exists for symbol, default to 0
            const tokenValue = VAULT_TOKEN_VALUE && VAULT_TOKEN_VALUE[symbol] ? VAULT_TOKEN_VALUE[symbol] : 0;
            vaultScore += totalStakedAmount * tokenValue;

            let activeBoost = 0;
            const tiersObj = VAULT_TIERS && VAULT_TIERS[symbol] ? VAULT_TIERS[symbol] : null;
            const tiers = tiersObj && tiersObj.tiers ? tiersObj.tiers : [];

            for (const tier of tiers) {
                if (totalStakedAmount >= tier.amount) activeBoost = tier.boost;
            }

            vaultSummary[symbol] = { totalStakedAmount, activeBoost };
        }

        return {
            id: walletAddress,
            vaultScore: parseFloat(vaultScore.toFixed(2)),
            vaultSummary,
        };
    });

    // 3. Sort descending by vaultScore, return top 100
    leaderboard.sort((a, b) => b.vaultScore - a.vaultScore);
    return leaderboard.slice(0, 100);
}
// ==================================================================
// LeaderboardPage Component
// The getVaultLeaderboardCallable prop has been removed; vault data
// is now fetched entirely on-chain via fetchOnChainVaultLeaderboard.
// ==================================================================
function LeaderboardPage({ playerData, leaderboardView, setLeaderboardView, handleClaimMonthlyReward, seasonClaim }) {
    const [leaderboard, setLeaderboard] = useState([]);
    const [vaultLeaderboard, setVaultLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLeaderboards = async () => {
            setLoading(true);
            try {
                if (leaderboardView === 'daily' || leaderboardView === 'seasonal') {
                    const dailyUrl = `${API_BASE_URL}/getDailyLeaderboard`;
                    const seasonalUrl = `${API_BASE_URL}/getSeasonalLeaderboard`;
                    const url = leaderboardView === 'daily' ? dailyUrl : seasonalUrl;
                    const response = await fetch(url);
                    const data = await response.json();
                    setLeaderboard(Array.isArray(data) ? data : []);
                } else if (leaderboardView === 'vault') {
                    // Fetch directly from the whatifvault1 smart contract
                    const data = await fetchOnChainVaultLeaderboard();
                    setVaultLeaderboard(Array.isArray(data) ? data : []);
                }
            } catch (error) {
                console.error("Failed to fetch leaderboard:", error);
                setLeaderboard([]);
            } finally {
                setLoading(false);
            }
        };
        fetchLeaderboards();
    }, [leaderboardView]);

    // Dynamic Info Banners
    const renderInfoBanner = () => {
        if (leaderboardView === 'daily') {
            return (
                <div className="bg-black/60 backdrop-blur-md border-l-4 border-orange-500 rounded-lg p-5 mb-6 text-slate-300 text-sm shadow-[0_0_15px_rgba(249,115,22,0.15)]">
                    <h3 className="text-xl font-bold text-orange-400 mb-2 flex items-center gap-2">🌞 Daily Sprint</h3>
                    <p className="mb-1"><strong>Goal:</strong> Earn points by performing Dungeon Runs.</p>
                    {!isNative && <p className="mb-1"><strong>Rewards:</strong> NFT drops are based on NFT Boosts.</p>}
                    <p className="mb-1"><strong>Reset:</strong> Every day at 08:00 AM ET.</p>
                    <p className="text-zinc-500 italic mt-2">*At reset, Daily Points are wiped and added to your Monthly Score.*</p>
                </div>
            );
        } else if (leaderboardView === 'seasonal') {
            return (
                <div className="bg-black/60 backdrop-blur-md border-l-4 border-cyan-500 rounded-lg p-5 mb-6 text-slate-300 text-sm shadow-[0_0_15px_rgba(6,182,212,0.15)]">
                    <h3 className="text-xl font-bold text-cyan-400 mb-2 flex items-center gap-2">🏆 Monthly Season</h3>
                    <p className="mb-1"><strong>Goal:</strong> Accumulate points over the month.</p>
                    {!isNative && <p className="mb-1"><strong>Rewards:</strong> Share the season pool proportional to your score.</p>}
                    <p className="mb-1"><strong>Reset:</strong> 1st of every month at 9:00 AM ET.</p>
                    <p className="text-zinc-500 italic mt-2">*This is the sum of all your Daily Points this month.*</p>

                    {handleClaimMonthlyReward && (
                        <div className="mt-4 pt-4 border-t border-cyan-500/30">
                            {!seasonClaim && (
                                <p className="text-zinc-500 text-sm">Checking for claimable seasons…</p>
                            )}

                            {seasonClaim && seasonClaim.status === "error" && (
                                <p className="text-amber-400 text-sm">
                                    Couldn&apos;t reach the chain to check your rewards. Try again shortly.
                                </p>
                            )}

                            {seasonClaim && seasonClaim.status === "none" && (
                                <p className="text-zinc-400 text-sm">
                                    No season is ready to claim yet.
                                    {seasonClaim.openSeasonId
                                        ? ` Season ${seasonClaim.openSeasonId} is still running — it closes on the 1st.`
                                        : ""}
                                </p>
                            )}

                            {seasonClaim && seasonClaim.status === "open" && (
                                <>
                                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 mb-4">
                                        <div>
                                            <span className="text-zinc-500 text-xs uppercase tracking-wide block">Season</span>
                                            <span className="text-white font-bold text-lg">{seasonClaim.seasonId}</span>
                                        </div>
                                        <div>
                                            <span className="text-zinc-500 text-xs uppercase tracking-wide block">Your points</span>
                                            <span className="text-white font-bold text-lg tabular-nums">
                                                {(seasonClaim.myPoints || 0).toLocaleString()}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-zinc-500 text-xs uppercase tracking-wide block">Season pool</span>
                                            <span className="text-white font-bold text-lg tabular-nums">
                                                {seasonClaim.totalPool.toLocaleString(undefined, { maximumFractionDigits: 0 })} {seasonClaim.symbol}
                                            </span>
                                            <span className="text-zinc-500 text-xs block tabular-nums">
                                                {seasonClaim.pool.toLocaleString(undefined, { maximumFractionDigits: 0 })} by points + {(seasonClaim.totalPool - seasonClaim.pool).toLocaleString(undefined, { maximumFractionDigits: 0 })} lottery
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-zinc-500 text-xs uppercase tracking-wide block">Your share</span>
                                            <span className="text-cyan-400 font-bold text-lg tabular-nums">
                                                {(seasonClaim.myShare || 0).toLocaleString(undefined, { maximumFractionDigits: 5 })} {seasonClaim.symbol}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                        <button
                                            onClick={() => handleClaimMonthlyReward(seasonClaim.seasonId)}
                                            disabled={seasonClaim.alreadyClaimed || !seasonClaim.myPoints}
                                            className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed text-white font-bold py-2 px-6 rounded transition-colors whitespace-nowrap"
                                        >
                                            {seasonClaim.alreadyClaimed
                                                ? "Already Claimed"
                                                : `Claim Season ${seasonClaim.seasonId}`}
                                        </button>
                                        <span className="text-xs text-zinc-500 max-w-sm">
                                            {seasonClaim.alreadyClaimed
                                                ? `You've already claimed season ${seasonClaim.seasonId}.`
                                                : !seasonClaim.myPoints
                                                    ? `You earned no points in season ${seasonClaim.seasonId}, so there is nothing to claim.`
                                                    : "Paid straight to your wallet. You sign the transaction yourself."}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            );
        } else {
            return (
                <div className="bg-black/60 backdrop-blur-md border-l-4 border-yellow-500 rounded-lg p-5 mb-6 text-slate-300 text-sm shadow-[0_0_15px_rgba(234,179,8,0.15)]">
                    <h3 className="text-xl font-bold text-yellow-400 mb-2 flex items-center gap-2">🏦 Vault Leaderboard</h3>
                    <p className="mb-1"><strong>Goal:</strong> Stake tokens to earn APR and Game Boosts.</p>
                    <p className="mb-1"><strong>Investor Requirement:</strong> You need a Vault Score of 250,000+ to qualify for Daily Rewards.</p>
                </div>
            );
        }
    };

    const renderLeaderboardTable = () => {
        const getRowClass = (index) => {
            if (index === 0) return 'bg-yellow-900/20 border-l-2 border-yellow-500';
            if (index === 1) return 'bg-zinc-300/10 border-l-2 border-zinc-400';
            if (index === 2) return 'bg-orange-900/20 border-l-2 border-orange-600';
            return 'hover:bg-zinc-800/50 transition-colors border-l-2 border-transparent';
        };

        if (leaderboardView === 'vault') {
            return (
                <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-black/40 backdrop-blur-sm">
                    <table className="w-full text-left text-sm text-zinc-300">
                        <thead className="bg-zinc-900/80 text-zinc-400 uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4 font-semibold text-yellow-500">Rank</th>
                                <th className="px-6 py-4 font-semibold text-yellow-500">Wallet</th>
                                <th className="px-6 py-4 font-semibold text-yellow-500">Vault Score</th>
                                <th className="px-6 py-4 font-semibold text-yellow-500">Staked Tokens</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800/50">
                            {vaultLeaderboard.map((player, index) => (
                                <tr key={player.id} className={getRowClass(index)}>
                                    <td className="px-6 py-4 whitespace-nowrap font-mono">
                                        {index + 1}
                                        {index === 0 && ' 👑'}
                                        {index === 1 && ' 🥈'}
                                        {index === 2 && ' 🥉'}
                                    </td>
                                    <td className="px-6 py-4 font-mono font-bold text-white">{player.id}</td>
                                    <td className="px-6 py-4 font-mono text-green-400">{(player.vaultScore || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="px-6 py-4 text-xs">
                                        {player.vaultSummary && Object.entries(player.vaultSummary).map(([token, data]) => {
                                            if (data.totalStakedAmount > 0) {
                                                return `${data.totalStakedAmount.toLocaleString()} ${token}`;
                                            }
                                            return null;
                                        }).filter(Boolean).join(', ') || 'None'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        } else {
            return (
                <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-black/40 backdrop-blur-sm">
                    <table className="w-full text-left text-sm text-zinc-300">
                        <thead className="bg-zinc-900/80 text-zinc-400 uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4 font-semibold text-cyan-500">Rank</th>
                                <th className="px-6 py-4 font-semibold text-cyan-500">Player</th>
                                <th className="px-6 py-4 font-semibold text-cyan-500">Level</th>
                                <th className="px-6 py-4 font-semibold text-cyan-500">
                                    {leaderboardView === 'daily' ? 'Daily Points' : 'Monthly Score'}
                                </th>
                                {leaderboardView === 'daily' && <th className="px-6 py-4 font-semibold text-cyan-500">Vault Score</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800/50">
                            {leaderboard.length > 0 ? (
                                leaderboard.map((player, index) => (
                                    <tr key={player.id || `fallback-${index}`} className={getRowClass(index)}>
                                        <td className="px-6 py-4 whitespace-nowrap font-mono">
                                            {index + 1}
                                            {index === 0 && ' 👑'}
                                            {index === 1 && ' 🥈'}
                                            {index === 2 && ' 🥉'}
                                        </td>
                                        <td className="px-6 py-4 font-mono font-bold text-white">{player.id || 'Unknown'}</td>
                                        <td className="px-6 py-4 font-mono">{player.level || '-'}</td>
                                        <td className="px-6 py-4 font-mono text-orange-400">
                                            {leaderboardView === 'daily'
                                                ? (player.dailyPoints || 0).toLocaleString()
                                                : (player.leaderboardPoints || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            }
                                        </td>
                                        {leaderboardView === 'daily' && (
                                            <td className={`px-6 py-4 font-mono ${(player.vaultScore || 0) >= 250000 ? 'text-green-400' : 'text-red-400'}`}>
                                                {(player.vaultScore || 0).toLocaleString()}
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-zinc-500 font-mono">
                                        No players found. Start playing to get on the board!
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20 w-full max-w-5xl mx-auto">
                <div className="text-cyan-500 animate-pulse font-mono text-xl">Loading Leaderboard...</div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-5xl mx-auto p-4 font-sans mb-10">
            {!isNative && <TodaysAirdrops />}

            {/* Glowing Tabs */}
            <div className="flex justify-center gap-4 mb-8 flex-wrap">
                <button
                    onClick={() => setLeaderboardView('daily')}
                    className={`px-6 py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-all border ${leaderboardView === 'daily'
                        ? 'bg-orange-500/20 text-orange-400 border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.4)]'
                        : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500'
                        }`}
                >
                    Daily Sprint
                </button>
                <button
                    onClick={() => setLeaderboardView('seasonal')}
                    className={`px-6 py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-all border ${leaderboardView === 'seasonal'
                        ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]'
                        : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500'
                        }`}
                >
                    Monthly Season
                </button>
                {!isNative && (
                    <button

                    onClick={() => setLeaderboardView('vault')}
                                        className={`px-6 py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-all border ${leaderboardView === 'vault'
                                            ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.4)]'
                                            : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500'
                                            }`}
                                    >
                                        WHATIF Vault
                                    </button>
                )}

            </div>

            {renderInfoBanner()}

            <h3 className="text-2xl font-bold text-white text-center mb-6">Top 100 Players</h3>

            {playerData?.isBlacklisted && (
                <div className="bg-red-950/40 border border-red-900/50 rounded-lg p-4 mb-6 text-center">
                    <p className="text-red-500 font-bold">You are blacklisted and will not appear on leaderboards.</p>
                </div>
            )}

            {renderLeaderboardTable()}
        </div>
    );
}

export default LeaderboardPage;
